// S-04 Activity filters (UserFlows S-04 Activity; API.md §7.13): person and date, plus the page, all in the URL
// (Frontend §7.1 "Activity: page"). The person is a clinic user, "Super admins", "System" or "Ops CLI" (task 13
// assumption: no endpoint lists the actors, so the options are the clinic's users plus the actor types).
import { addDays, BOOKED_PRESETS, presetRange, rangeInstants, type LocalDate } from '@pa/ui'

export const ACTIVITY_PRESETS = BOOKED_PRESETS

export interface ActivitySearch {
  /** `user:{id}` or `type:super_admin` / `type:system` / `type:cli`. */
  actor?: string
  range?: string
  from?: LocalDate
  to?: LocalDate
  page?: number
}

export interface ActivityParams {
  actorUserId?: string
  actorType?: 'clinic_member' | 'super_admin' | 'system' | 'cli'
  from?: string
  to?: string
  page?: string
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const DATE = /^\d{4}-\d{2}-\d{2}$/
const TYPES = new Set(['super_admin', 'system', 'cli'])
const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined)

export function isActorValue(v: unknown): v is string {
  const s = str(v)
  if (!s) return false
  if (s.startsWith('user:')) return UUID.test(s.slice(5))
  return s.startsWith('type:') && TYPES.has(s.slice(5))
}

/** The URL → valid Activity filters (the date part is the same as C-03's). */
export function parseActivitySearch(raw: Record<string, unknown>): ActivitySearch {
  const out: ActivitySearch = {}
  if (isActorValue(raw.actor)) out.actor = raw.actor
  const range = str(raw.range)
  const from = str(raw.from)
  const to = str(raw.to)
  if (range === 'custom') {
    if (from && to && DATE.test(from) && DATE.test(to) && from <= to) Object.assign(out, { range, from, to })
  } else if (range && range !== 'any' && ACTIVITY_PRESETS.some((p) => p.id === range)) {
    out.range = range
  }
  const page = typeof raw.page === 'number' ? raw.page : Number(str(raw.page))
  if (Number.isInteger(page) && page > 1) out.page = page
  return out
}

/** #53's query: presets resolved at local midnight in the clinic's zone (`to` exclusive, API.md §1.4). */
export function toActivityParams(s: ActivitySearch, timeZone: string, today: LocalDate): ActivityParams {
  const params: ActivityParams = {}
  if (s.actor?.startsWith('user:')) params.actorUserId = s.actor.slice(5)
  if (s.actor?.startsWith('type:')) params.actorType = s.actor.slice(5) as ActivityParams['actorType']
  const range =
    s.range === 'custom' && s.from && s.to
      ? { from: s.from, to: addDays(s.to, 1) }
      : s.range
        ? presetRange(s.range, today)
        : null
  if (range) Object.assign(params, rangeInstants(range, timeZone))
  if (s.page) params.page = String(s.page)
  return params
}

export const hasActivityFilters = (s: ActivitySearch) => s.actor !== undefined || s.range !== undefined
